import React, { useState } from 'react';
import { Zap, CheckCircle2, ShieldCheck, Star, ArrowRight } from 'lucide-react';
import FastByNoFeeHowItWorksModal from './FastByNoFeeHowItWorksModal';

/**
 * FastByNoFeeSection — landing-page hero section that doubles as
 * "Locations 1 & 2" from the Fast by NoFee program spec:
 *   - 1) Near "0% Commission Fees" — explicit trust message.
 *   - 2) Inside "Why Restaurants Choose NoFee" — bullet feature row.
 *
 * Voice: trust + reliability, NEVER speed. Tagline locked to the spec.
 * Both CTAs open the FastByNoFeeHowItWorksModal so visitors get the
 * full "how it works" context BEFORE landing on the paid signup page.
 */
const FastByNoFeeSection = () => {
  const [howOpen, setHowOpen] = useState(false);
  return (
    <section
      className="relative bg-black py-20 lg:py-24 border-t border-white/[0.04]"
      data-testid="landing-fast-section"
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left — hero pitch */}
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400/15 border border-yellow-400/30 text-white text-[11px] font-black uppercase tracking-[0.18em] mb-4">
              <Zap className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              Fast by NoFee
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1]">
              On-Time Delivery
              <br />
              <span className="text-yellow-400">Verified.</span>
            </h2>
            <p className="text-base text-white/65 mt-5 leading-relaxed max-w-lg">
              <strong className="text-white">Verified by Real Customers. Delivered As Promised.</strong>
              <br />
              Restaurants earn trust by consistently delivering within the time they promise. Customers confirm each order after delivery — and that verification creates a competitive advantage that converts into more orders.
            </p>
            <p className="text-[12.5px] text-white/45 mt-3 max-w-lg">
              <span className="text-yellow-300/90 font-semibold">Note:</span> Fast by NoFee does not mean fastest delivery — it means <span className="text-white/80 font-semibold">On-Time Delivery Verified</span> by real customers.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setHowOpen(true)}
                data-testid="landing-fast-cta"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-zinc-950 font-bold text-sm transition-colors"
              >
                Earn On-Time Delivery Recognition <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setHowOpen(true)}
                data-testid="landing-fast-how"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 hover:border-white/30 text-white/85 hover:text-white font-bold text-sm transition-colors"
              >
                How it works
              </button>
            </div>
          </div>

          {/* Right — feature bullets ("Why Restaurants Choose NoFee" entry) */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6 sm:p-8">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-bold mb-5">
              Why Restaurants Choose NoFee
            </p>
            <ul className="space-y-5">
              <FeatureRow
                Icon={CheckCircle2}
                title="0% Commission Fees"
                body="Keep 100% of your revenue. No marketplace cut, no surprise fees."
                tone="emerald"
              />
              <FeatureRow
                Icon={Zap}
                title="⚡ Fast by NoFee — On-Time Delivery Verified"
                body="Earn customer trust through verified on-time deliveries — your delivery promise becomes your competitive advantage."
                tone="yellow"
              />
              <FeatureRow
                Icon={Star}
                title="Rewards Program"
                body="Increase customer engagement and repeat orders with built-in NoFee Rewards Points."
                tone="white"
              />
              <FeatureRow
                Icon={ShieldCheck}
                title="Multiple Ordering Channels"
                body="SMS, WhatsApp, Email and Phone Orders — meet your customers where they are."
                tone="white"
              />
            </ul>
          </div>
        </div>
      </div>
      <FastByNoFeeHowItWorksModal
        open={howOpen}
        onClose={() => setHowOpen(false)}
        ctaSource="landing_fast_section"
      />
    </section>
  );
};

const FeatureRow = ({ Icon, title, body, tone }) => {
  const cls = {
    emerald: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
    yellow: 'bg-yellow-400/15 border-yellow-400/30 text-yellow-300',
    white: 'bg-white/[0.06] border-white/15 text-white',
  }[tone] || 'bg-white/[0.06] border-white/15 text-white';
  return (
    <li className="flex items-start gap-3.5">
      <span className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${cls}`}>
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <p className="text-[14.5px] font-bold text-white">{title}</p>
        <p className="text-[13px] text-white/65 leading-relaxed mt-0.5">{body}</p>
      </div>
    </li>
  );
};

export default FastByNoFeeSection;
